import { QRCodeSVG } from 'qrcode.react';

import { HTTP_GET } from 'webclient.constants';
import { useCurrentClassVar } from 'approot/classes/classes.hooks';
import useDownloadAndPrintPdf from 'approot/shared/print-pdf/print-pdf.hooks';
import { SecondaryLoadingButton } from 'approot/shared/styles/buttons/loading-button';
import { useAccessibleHeading } from 'approot/shared/ally/accessible-heading';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import AssignmentsFeedback from 'approot/shared/assignments-feedback/assignments-feedback';
import { Box } from 'approot/shared/design-system/box/box.style';
import { HrStyled } from 'approot/shared/styles/decorative.style';
import {
  BoldStyled,
  Heading18,
  OlStyled,
} from 'approot/shared/styles/typography.style';
import {
  SigninTypeClassCode,
  SigninTypeQrCode,
} from '../signin/signin-type/signin-type';
import IconPrint from 'approot/shared/icons/icon-print';
import { SimpleModalContentStyled } from 'lib/modal/simple-modal.styles';
import {
  AssignClassOr,
  AssignClassActions,
  AssignClassDetail,
  AssignClassBox,
} from './assign-class-detail-modal.style';
import {
  ListInquisitiveLink,
  ListStudentSignIn,
} from './assign-class-details-shared-list';
import * as Styled from './assign-class-details-unauthenticated.style';
import { SIGN_IN_CARDS_PDF_FILENAME } from './assign-print.constants';
import { getUserClassPath } from 'approot/classes/classes.apollo';
import {
  NotificationActions,
  NotificationContent,
  NotificationTip,
  NotificationTipText,
} from 'approot/shared/notifications/notification-tip.style';
import { SecondaryButtonAndIconLink } from 'approot/shared/styles/buttons/secondary-button.style';
import { PRIVATE_MY_STUDENTS } from 'global/private-routes.constants';
import { openNewTab } from 'approot/shared/logic/open-new-tab';
import IconExpand from 'approot/shared/icons/icon-expand';
import { getRegionalisedLink } from 'approot/shared/logic/get-regionalised-link';
import { addStudentsPushDataTrackingEvent } from 'approot/teacher-student/logic/add-students-push-data-tracking-event';
import { EVENT_TARGET_TYPE_BANNER } from 'approot/shared/data-tracker/data-events.constants';

const AssignClassDetailsUnauthenticated = () => {
  const currentClass = useCurrentClassVar();
  const classQRCodeUrl = currentClass?.classToDoQRCodeUrl || '';
  const classPassCode = currentClass?.classToDoPasscode?.toString();
  const classId = currentClass?.id;
  const classToDoId = currentClass?.classToDoId;

  const { registerHeading } = useAccessibleHeading();
  const { showError } = useSnackbar();

  const { handlePrintClick, loading } = useDownloadAndPrintPdf({
    downloadPath: '/api/classtodo/print-password',
    downloadFileName: SIGN_IN_CARDS_PDF_FILENAME,
    downloadMethod: HTTP_GET,
    onError: () =>
      showError(
        'There was a problem generating the printing file. Please try again later.'
      ),
  });

  return (
    <SimpleModalContentStyled>
      <ScreenReaderOnly as="h1" tabIndex={-1} ref={registerHeading}>
        How Your Class Sign In
      </ScreenReaderOnly>
      <NotificationTip>
        <NotificationContent>
          <NotificationTipText>
            Students who sign in with a class code can view content but can’t
            respond. To collect student responses, add your students and have
            them sign in using a different method.
          </NotificationTipText>
          <NotificationActions>
            <SecondaryButtonAndIconLink
              $isSmall
              data-testid="btn-add-students"
              to={getRegionalisedLink(getUserClassPath(PRIVATE_MY_STUDENTS))}
              onClick={e => {
                e.preventDefault();
                addStudentsPushDataTrackingEvent({
                  targetType: EVENT_TARGET_TYPE_BANNER,
                  targetIdentifier: 'how-your-class-sign-in',
                });
                openNewTab(
                  getRegionalisedLink(getUserClassPath(PRIVATE_MY_STUDENTS))
                );
              }}
            >
              Add Students
              <IconExpand />
            </SecondaryButtonAndIconLink>
          </NotificationActions>
        </NotificationContent>
      </NotificationTip>

      <AssignClassBox>
        <AssignClassDetail>
          <Box $alignItems="center" $gap={8} $marginBottom={16}>
            <SigninTypeQrCode />
            <Heading18 as="h2" marginBottom={0}>
              Sign in with QR Code
            </Heading18>
          </Box>
          <OlStyled>
            <ListInquisitiveLink />
            <ListStudentSignIn />
            <li>
              Choose <strong>QR Code</strong> and scan the qr code{' '}
              <strong>below:</strong>
            </li>
          </OlStyled>
          <Styled.AssignQrContainer>
            <QRCodeSVG value={classQRCodeUrl} size={160} />
          </Styled.AssignQrContainer>
        </AssignClassDetail>
        <AssignClassOr>
          <span>OR</span>
        </AssignClassOr>
        <AssignClassDetail>
          <Box $alignItems="center" $gap={8} $marginBottom={16}>
            <SigninTypeClassCode />
            <Heading18 as="h2" marginBottom={0}>
              Sign in with Class Code
            </Heading18>
          </Box>
          <OlStyled>
            <ListInquisitiveLink />
            <ListStudentSignIn />
            <li>
              Choose <strong>Class Code</strong> and enter the details below:
            </li>
          </OlStyled>
          <Styled.AssignClassCodes>
            <Styled.AssignClassCodesLabel>
              Class Code
            </Styled.AssignClassCodesLabel>
            <BoldStyled marginBottom={0} data-selector="class-code">
              {classToDoId}
            </BoldStyled>
          </Styled.AssignClassCodes>
          <Styled.AssignClassCodes>
            <Styled.AssignClassCodesLabel>
              Access Code
            </Styled.AssignClassCodesLabel>
            <BoldStyled marginBottom={0} data-selector="access-code">
              {classPassCode}
            </BoldStyled>
          </Styled.AssignClassCodes>
        </AssignClassDetail>
      </AssignClassBox>

      <HrStyled $marginTop={0} $marginBottom={24} />

      <AssignClassActions>
        <AssignmentsFeedback buttonText="Give us Feedback" isButton />
        {classId && (
          <SecondaryLoadingButton
            type="button"
            disabled={loading}
            isLoading={loading}
            isIconAndText
            onClick={() =>
              handlePrintClick({
                userClassId: classId,
              })
            }
            data-selector="modal-btn-print"
          >
            <IconPrint width={20} height={20} />
            Print Sign In Cards
          </SecondaryLoadingButton>
        )}
      </AssignClassActions>
    </SimpleModalContentStyled>
  );
};

export default AssignClassDetailsUnauthenticated;
